import { IconEye } from '@lib/icons'
import { ThemeColors } from '@octopus-do/octopus-storybook'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import {
  changeConfirmClosing,
  changeLockModal,
  openSystemDialog,
} from '@storeOld/reducers/ui'
import cx from 'classnames'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import type { Comment as CommentType, Recipient } from '@/types/models'
import { UNSAVED_CHANGES } from '../../App/SystemDialogs/constants'
import css from './Comments.module.scss'
import { CommentsForm } from './components/CommentsForm'
import { CommentsList } from './components/CommentsList'
import { CommentsPlaceholder as Placeholder } from './components/Placeholder'
import type { Payload as PayloadType } from './types'

type Classnames = {
  container?: string
}

type Props = {
  author: Recipient | null
  classnames?: Classnames
  comments: CommentType[]
  localStorageKey: string
  loggedIn: boolean
  focus?: boolean
  onCommit: (payload: any) => void
  onLogin: () => void
  onUpdate: (payload: PayloadType) => void
}

export const Comments: React.FC<Props> = ({
  author,
  classnames,
  comments,
  localStorageKey,
  loggedIn,
  focus,
  onCommit,
  onLogin,
  onUpdate,
}) => {
  const dispatch = useDispatch()

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null)
  const anonymousComment = useSelector(
    migrationProjectSelector.getAnonymousComments
  )

  const canComment = loggedIn || anonymousComment

  const cn: Classnames = {
    container: cx(
      (classnames?.container && css[classnames?.container]) ??
        classnames?.container,
      css.container
    ),
  }

  const handleUpdate = (payload: PayloadType) => {
    onUpdate(payload)
  }

  const confirmClosing = useCallback(
    (): Promise<boolean> =>
      new Promise((resolve) => {
        dispatch(
          openSystemDialog(UNSAVED_CHANGES, {
            callback: resolve,
          })
        )
      }),
    []
  )

  useEffect(() => {
    if (editingCommentId && loggedIn) {
      dispatch(changeLockModal(true))
      dispatch(changeConfirmClosing(confirmClosing))
    } else {
      dispatch(changeLockModal(false))
      dispatch(changeConfirmClosing())
    }
  }, [editingCommentId])

  return (
    <div className={cn.container}>
      <div className={ThemeColors.light}>
        <CommentsList
          comments={comments}
          onUpdate={handleUpdate}
          setEditingCommentId={setEditingCommentId}
        />
        {!canComment && (
          <>
            <div className={css.placeholder}>
              <IconEye />
              <span>
                Only registered users can
                <br /> view comments
              </span>
            </div>
            <Placeholder onLogin={onLogin} />
          </>
        )}

        <CommentsForm
          author={author}
          localStorageKey={localStorageKey}
          showForm={canComment}
          asGuest={canComment && !loggedIn}
          focus={focus}
          onCommit={onCommit}
          editingCommentId={editingCommentId}
        />
      </div>
    </div>
  )
}
